import { Add, Cancel, Search } from "@mui/icons-material";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setRegularization } from "../../store/reducers/regularizationSlice";

const CCDropdown = () => {
  const { employeesList } = useSelector((state) => state.notification);
  const { regularization } = useSelector((state) => state.regularization);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();

  const filteredData = employeesList?.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleAddClick = () => {
    setIsOpen(true);
  };

  const handleItemSelect = (item) => {
    const isSelected = selectedItems.some(
      (selected) => selected.employeeId === item.employeeId
    );

    if (isSelected) {
      dispatch(
        setRegularization({
          ...regularization,
          cc: selectedItems.filter((selected) => selected.employeeId !== item.employeeId),
        })
      );
      setSelectedItems(
        selectedItems.filter((selected) => selected.employeeId !== item.employeeId)
      );
    } else {
      dispatch(
        setRegularization({
          ...regularization,
          cc: [...selectedItems, item],
        })
      );
      setSelectedItems([...selectedItems, item]);
    }
  };

 const getInitials = (name) => {
   const cleanName = name.split("(")[0].trim();
   const nameParts = cleanName.split(" ");
   if (nameParts.length >= 2) {
     return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
   }
   return cleanName.substring(0, 2).toUpperCase();
 };

  const getIdFromName = (name) => {
    const match = name.match(/\(([^)]+)\)$/);
    return match ? match[1] : "";
  };

  return (
    <div className="max-w-md bg-white">
      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={handleAddClick}
            className="flex items-center justify-center w-12 h-12 border-2 border-dashed border-gray-300 rounded-full hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
          >
            <Add size={20} className="text-[#A3B2C7]" />
          </button>
          
          {selectedItems.length > 0 ? (
              <div className="flex items-center gap-2">
                {selectedItems.map((item) => (
                  <div
                    key={item.employeeId}
                    className="group rounded-lg relative"
                  >
                    <div className="group-hover:opacity-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
                      {getInitials(item.name)}
                    </div>
                    <div className="flex items-center justify-center">
                      <button
                        onClick={() => handleItemSelect(item)}
                        className="opacity-0 group-hover:opacity-100 p-1 bg-red-100 rounded-full transition-all flex items-center justify-center duration-200 absolute inset-0 m-auto w-8 h-8"
                      >
                        <Cancel size={14} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
          ) : (
            <span className="text-sm text-[#A3B2C7]">
              {isOpen ? "Adding..." : "Add"}
            </span>
          )}
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 mt-1">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for name, ID etc."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filteredData.length > 0 ? (
                filteredData.map((item) => {
                  const isSelected = selectedItems.some(
                    (selected) => selected.employeeId === item.employeeId
                  );
                  return (
                    <button
                      key={item.employeeId}
                      onClick={() => handleItemSelect(item)}
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-blue-50 border-r-2 border-blue-500"
                          : ""
                      }`}
                    >
                      <div
                        className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                          isSelected
                            ? "bg-blue-500 border-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>

                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                        {getInitials(item.name)}
                      </div>

                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800">
                          {item.name.split("(")[0].trim()}
                        </div>
                        <div className="text-xs text-gray-500 text-left">
                          {getIdFromName(item.name)}
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No results found for "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CCDropdown;
